import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AudioApi } from '../../api/AudioApi';
import { DialogPromptConversationListen, SPEAKER_NAME_A, SPEAKER_NAME_B } from '../../api/DialogPromptApi';
import { DialogPromptConversationApi, IDialogPromptConversation } from '../../api/DialogPromptConversationApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { useKaiTiFontStyles } from '../../font/KaiTiFont';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherAudioActions, getConversationSpeakerRole } from '../TeacherAudioReportButton';
import { getAudioByText } from './DialogPromptComponent';

interface DialogPromptConversationListenComponentProps {
    dialogPrompt: DialogPromptConversationListen;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptConversationListenComponent: React.FC<DialogPromptConversationListenComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const kaiTiFontClass = useKaiTiFontStyles();

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [dialogPromptIndex, setDialogPromptIndex] = React.useState(0);
    const [conversation, setConversation] = React.useState<IDialogPromptConversation | null>(null);
    const dialogPromptIndexRef = React.useRef(0); // Initialize the ref outside the component function

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);


    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        DialogPromptConversationApi.getById(dialogPrompt.dialog_prompt_conversation_id)
            .then(response => {
                setConversation(response.data)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [clearFooterList, dialogPrompt.dialog_prompt_conversation_id, dialogPrompt.prompt, history, location, setErrorMessage]);

    const handleSpeakerClick = React.useCallback((dialogPromptIndex: (number | null) = null) => {
        let audioPromise;
        const advanceDialogPromptIndexRef = dialogPromptIndex == null || dialogPromptIndex == dialogPromptIndexRef.current;
        if (dialogPromptIndex == null) {
            dialogPromptIndex = dialogPromptIndexRef.current
        }

        if (conversation) {
            const sentence = conversation.dialog_prompt_sentence_list[dialogPromptIndex]
            if (sentence.speaker_name == SPEAKER_NAME_A) {
                audioPromise = AudioApi.getSpeakerA(sentence.sentence_id)
            } else if (sentence.speaker_name == SPEAKER_NAME_B) {
                audioPromise = AudioApi.getSpeakerB(sentence.sentence_id)
            } else {
                throw Error(`Unknown speaker name ${sentence.speaker_name}`)
            }

            audioPromise
                .then(response => {
                    // check if the response was successful
                    if (response.status >= 300) {
                        throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                    }
                    return response.data;
                })
                .then(blob => {
                    playAudio(blob, {
                        onEnded: () => {
                            if (advanceDialogPromptIndexRef) {
                                dialogPromptIndexRef.current += 1;
                                if (dialogPromptIndexRef.current >= conversation.dialog_prompt_sentence_list.length) {
                                    setDialogPromptComplete(dialogPrompt.id)
                                } else {
                                    setDialogPromptIndex(dialogPromptIndexRef.current);
                                }
                            }
                        }
                    });

                    // create a new Audio object and set the source to the blob URL
                    // const audio = new Audio(URL.createObjectURL(blob));
                    // audio.addEventListener('ended', () => {
                    //     if (advanceDialogPromptIndexRef) {
                    //         dialogPromptIndexRef.current += 1;
                    //         if (dialogPromptIndexRef.current >= conversation.dialog_prompt_sentence_list.length) {
                    //             setDialogPromptComplete(dialogPrompt.id)
                    //         } else {
                    //             setDialogPromptIndex(dialogPromptIndexRef.current);
                    //         }
                    //     }
                    // }); // Listen to the 'ended' event
                    // audio.play();
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [conversation, dialogPrompt.id, dialogPromptIndexRef, playAudio, setDialogPromptComplete, setDialogPromptIndex, history, location, setErrorMessage]);

    const buildConversationAudioReportContext = React.useCallback((sentenceIndex: number) => {
        if (!conversation) return null;
        const sentence = conversation.dialog_prompt_sentence_list[sentenceIndex];
        if (!sentence) return null;
        return {
            sentenceId: sentence.sentence_id,
            dialogPromptId: dialogPrompt.id,
            textToSpeechSpeakerId: getConversationSpeakerRole(sentence.speaker_name),
            audioText: sentence.sentence_text ?? null,
            audioRole: `conversation_speaker_${sentence.speaker_name ?? ''}`,
        };
    }, [conversation, dialogPrompt.id]);

    React.useEffect(() => {
        if (chatBubbleDone) {
            if (chatBubbleContainerRef.current) {
                scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
            }
            setActionList([
                <TeacherAudioActions
                    context={buildConversationAudioReportContext(dialogPromptIndexRef.current)}
                    onPlay={() => handleSpeakerClick()}
                    showReportMenu={false}
                />
            ])
        }
    }, [chatBubbleDone, chatBubbleContainerRef, handleSpeakerClick, setActionList, buildConversationAudioReportContext, dialogPromptIndex]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [dialogPromptIndex, chatBubbleContainerRef])


    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
                dialogPromptId={dialogPrompt.id}
                audioRole="prompt"
            />
            {chatBubbleDone && conversation && (
                conversation.dialog_prompt_sentence_list.slice(0, dialogPromptIndex + 1).map((sentence, sentenceIndex) => (
                    <Box display="inline-block">
                        <Box display="inline-flex" width="70px" /> {/* This acts as a spacer */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent='flex-start'
                            key={sentenceIndex}
                            sx={{
                                mx: 1, py: 2,
                                display: 'inline-flex',
                                backgroundColor: sentenceIndex % 2 === 0 ? '#FFFFFF' : '#F2F2F2', // Example color condition
                            }} // Add margin to create space between Stacks
                            className='border-dark'
                        >
                            <Box sx={{ borderRight: '1.5px solid rgba(82, 99, 125, 0.8)', pr: '16px', display: 'flex', alignItems: 'center', mr: '8px' }}>
                                {sentenceIndex % 2 === 0 && (<>
                                    <Typography sx={{
                                        fontSize: '36px',
                                        pl: '16px',
                                    }}>A</Typography>
                                </>)}
                                {sentenceIndex % 2 !== 0 && (<>
                                    <Typography sx={{
                                        fontSize: '36px',
                                        pl: '16px',
                                    }}>B</Typography>
                                </>)}
                            </Box>
                            <TeacherAudioActions
                                context={buildConversationAudioReportContext(sentenceIndex)}
                                onPlay={() => handleSpeakerClick(sentenceIndex)}
                            />
                            {dialogPrompt.class_name != "DialogPromptConversationListenWithoutCharacterModel" &&
                                <div className={kaiTiFontClass.customFont} style={{ fontSize: '36px', paddingLeft: '16px', margin: 0, padding: 0, lineHeight: 0.9 }} >
                                    <span>{sentence.sentence_text}</span>
                                </div>
                            }
                        </Stack>
                    </Box>
                ))
            )}
        </Stack >
    );
};

export default DialogPromptConversationListenComponent;
